import { useState, useEffect, useCallback } from 'react'
import { SupabaseClient } from '@supabase/supabase-js'
import type { ActivityLog } from '../types'

interface UseActivityLogsOptions {
  search?: string
  action?: string
  limit?: number
}

export function useActivityLogs(client: SupabaseClient | null, options: UseActivityLogsOptions = {}) {
  const { search = '', action = '', limit = 100 } = options

  const [logs, setLogs] = useState<ActivityLog[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchLogs = useCallback(async () => {
    if (!client) {
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      let query = client
        .from('admin_activity_log')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(limit)

      if (action) {
        query = query.eq('action', action)
      }

      if (search) {
        query = query.or(`table_name.ilike.%${search}%,action.ilike.%${search}%`)
      }

      const { data, error: queryError } = await query

      if (queryError) throw queryError
      setLogs(data || [])
    } catch (err: any) {
      console.error('Error fetching activity logs:', err)
      setError(err.message)
    } finally {
      setIsLoading(false)
    }
  }, [client, search, action, limit])

  useEffect(() => {
    fetchLogs()
  }, [fetchLogs])

  return { logs, isLoading, error, refetch: fetchLogs }
}
